import { getLocalizedTitle, getLocalizedDescription } from '@immopersian/common';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { CourseSession } from '@immopersian/common';

interface SessionSidebarProps {
  sessions: CourseSession[];
  selectedSessionId: string | null;
  hasAccess: boolean;
  freeSessions: CourseSession[];
  onSessionClick: (sessionId: string, isFree: boolean) => void;
}

export function SessionSidebar({
  sessions,
  selectedSessionId,
  hasAccess,
  freeSessions,
  onSessionClick,
}: SessionSidebarProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="w-full lg:w-80 bg-brand-beige border-r border-brand-dark lg:overflow-y-auto">
      <div className="p-4 border-b border-brand-dark bg-brand-beige-dark">
        <h2 className="font-semibold text-lg text-brand-dark">{t('courses.courseContent')}</h2>
        <p className="text-sm text-brand-dark mt-1">
          {sessions.length} {t('courses.sessions')}
          {!hasAccess && freeSessions.length > 0 && (
            <span className="text-brand-terracotta"> • {freeSessions.length} {t('courses.freeSessions')}</span>
          )}
        </p>
      </div>
      
      {sessions.length > 0 ? (
        <div className="divide-y">
          {sessions.map((session, index) => {
            const isAccessible = hasAccess || session.is_free;
            const isSelected = selectedSessionId === session.id;
            
            return (
              <button
                key={session.id}
                onClick={() => onSessionClick(session.id, session.is_free)}
                className={`w-full text-left p-4 transition-colors ${
                  isSelected 
                    ? 'bg-brand-terracotta border-l-4 border-brand-dark' 
                    : isAccessible 
                      ? 'hover:bg-brand-beige-dark'
                      : 'bg-gray-100 opacity-75 hover:opacity-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    isSelected 
                      ? 'bg-brand-dark text-white' 
                      : isAccessible
                        ? 'bg-white text-brand-dark'
                        : 'bg-gray-300 text-gray-600'
                  }`}>
                    {isAccessible ? index + 1 : <Lock className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium text-sm ${
                        isSelected 
                          ? 'text-white' 
                          : isAccessible
                            ? 'text-brand-dark'
                            : 'text-gray-600'
                      }`}>
                        {getLocalizedTitle(session, i18n.language)}
                      </h3>
                      {session.is_free && !hasAccess && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          {t('courses.freePreview')}
                        </span>
                      )}
                    </div>
                    {session.description && (
                      <div className={`text-xs mt-1 line-clamp-2 prose prose-xs max-w-none ${
                        isSelected 
                          ? 'text-white/90 prose-invert' 
                          : isAccessible
                            ? 'text-brand-dark/70'
                            : 'text-gray-500'
                      }`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {getLocalizedDescription(session, i18n.language)}
                        </ReactMarkdown>
                      </div>
                    )}
                    {!isAccessible && (
                      <p className="text-xs mt-1 text-brand-terracotta font-medium">
                        {t('courses.lockedSessionMessage')}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="p-4 text-center text-brand-dark">
          {t('courses.noSessions')}
        </div>
      )}
    </div>
  );
}

